// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import Big from 'big.js';

import { calculateBogoDiscounts, determineBogoDiscounts } from './bogo';
import { calculateCouponDiscounts, prepareCoupon } from './coupons';
import { calculateRewardDiscounts, prepareRewards } from './rewards';
import { calculateSaleDiscounts } from './sales';
import {
  prepareProductTotal,
  redistributePennies,
  calculateMenuTotal,
  findAPlaceForResidualPennies,
  calculateManualDiscounts,
  calculateTip,
  calculatePostTaxSubtotal,
  orderProductsForDiscountBehavior,
  getGlobalSpecialsSettings,
} from './utilities';
import { determineMixAndMatchAdjustments } from './mix-and-match';
import { Receipt } from './receipt';
import { taxes } from './taxes';
import { calculateStackingCompoundingPercentDtc } from './bogo/calculate-stacking-compounding-percent-dtc';
import { redistributeDiscountToCart } from './bogo/redistribute-discount-to-cart';

import { compareStowedDiscounts } from './discount-comparison';
import { addCanadianDeliveryFeeTaxIfApplicable, calculateDeliveryFee, calculatePaymentFeeIfApplicable } from './fees';

export function computer({
  cart,
  dispensary,
  deliveryOption,
  medicalOrder,
  couponValue,
  manualDiscount,
  tipValue,
  paymentMethod,
  toMenu,
  appliedRewards,
}) {
  if (!dispensary || !cart) {
    return {};
  }

  /*
   * One thing to realize is that at this point we have prices in SpecialPrices
   * which are discount first and then tax.  The regular Prices arrays contain
   * just the tax.  So if you later want to build something a discount following
   * a tax take the regular price and apply the discount.  Or if you just want the
   * discount rip off the tax from the SpecialPrices.  The tools for building different
   * price combinations exist.
   */
  const { details, salesTaxExistence, cannabisTaxExistence, bottleDepositTaxExistence } = taxes(
    cart,
    dispensary,
    medicalOrder,
    deliveryOption === 'delivery',
    toMenu
  );
  const receipt = new Receipt(dispensary);
  const globalSpecialsSettings = getGlobalSpecialsSettings(dispensary);

  // Check if there's any individual non-stacking special
  const allSpecials = _.flatMap(details, (detail) => [
    ...(detail.specialData?.saleSpecials || []),
    ...(detail.specialData?.bogoSpecials || []),
  ]);

  const hasIndividualNonStackingSpecial = _.some(
    allSpecials,
    (special) => special.stackingMode === 'non-stacking' || special.discountStacking === false
  );

  /* sort order here will depend on the dispensaries special config
   * if the dispensary has stacking disabled with favorCustomer selected we want to sort the products from most
   * expensive to cheapest so that the discounts are larger (favor customer) otherwise we want to
   * sort the details by cheapest to most expensive menu price (includes sales discounts), so discounts / specials /
   * rewards / etc are applied to cheapest applicable items before anything else
   */
  let detailsSorted = orderProductsForDiscountBehavior(details, globalSpecialsSettings);

  // If Mix and Match is enabled, this will determine viable discounts, adding a mixAndMatch object with an
  // adjustedBasePrice to be used instead of basePrice for calculations later on
  detailsSorted = determineMixAndMatchAdjustments(detailsSorted, dispensary, deliveryOption === `delivery`);

  const discountTaxOrder = _.get(dispensary, `taxConfig.discountTaxOrder`, `both`);
  let { productTotal, residualPrice } = prepareProductTotal(detailsSorted);

  let credit = Big(0);
  // this is a workaround to prevent a duplicate line item at checkout which us a legacy artifact
  let rewardAppliedToCredit = false;

  const products = {};
  _.forEach(cart, (item) => (products[`${item.product._id}_${item.option || 'N/A'}`] = item));

  let { coupon, couponApplicableSubtotal, couponApplicableSubtotalPlusTaxes, couponLeftOver } = prepareCoupon({
    couponValue,
    details: detailsSorted,
    discountTaxOrder,
    medicalOrder,
    products,
  });

  /* eslint-disable prefer-const */
  let {
    rewardApplicableSubtotal,
    rewardApplicableSubtotalPlusTaxes,
    rewardCredit,
    rewardLeftOver,
    rewards,
  } = prepareRewards({
    appliedRewards,
    details,
    products,
  });
  /* eslint-enable prefer-const */
  let bogoCalcsData = {};
  let bogoRewardsApplied = {};
  let bottleDepositTaxCentsTotal = Big(0);
  let cannabisTaxTotal = Big(0);
  let salesTaxTotal = Big(0);
  let avoidTaxes = false;
  let residual;

  bogoCalcsData = determineBogoDiscounts({
    bogoCalcsData,
    detailsSorted,
    discountTaxOrder,
    medicalOrder,
    globalSpecialsSettings,
  });

  _.forEach(detailsSorted, (detail, index) => {
    const { quantity, specialData, wholesalePrice } = detail;
    const basePrice = detail.mixAndMatch?.adjustedBasePrice || detail.basePrice;
    const productNonArmsLength = products[detail.key].product.nonArmsLength;
    const receiptDetail = receipt.addProduct(detail.id, quantity, basePrice.times(100), detail.option);

    const mixAndMatchDiscountAmount = Big(detail.mixAndMatch?.discountAmount || 0);
    if (mixAndMatchDiscountAmount.gt(0)) {
      receiptDetail.addMixAndMatch({ type: `originalBasePrice`, value: detail.basePrice.times(100) });
      receiptDetail.addMixAndMatch({ type: `adjustedBasePrice`, value: basePrice.times(100) });
      receiptDetail.addMixAndMatch({ type: `total`, value: mixAndMatchDiscountAmount.times(100) });
    }

    const applicableBogoRewards = bogoCalcsData?.applicableRewards?.[detail.key] || [];

    const price = basePrice.times(quantity);
    const qtyWholesalePrice = wholesalePrice.times(quantity);
    const whPrice = qtyWholesalePrice.round(2, 1);
    let bottleDepositTaxCents = Big(0);
    let cannabisTax = Big(0);
    let salesTax = Big(0);

    if (discountTaxOrder === 'both' || discountTaxOrder === 'taxesFirst') {
      bottleDepositTaxCents = detail.bottleDepositTaxCents.times(detail.quantity);
      cannabisTax = detail.cannabisTax(price, whPrice, productNonArmsLength);
      salesTax = detail.salesTax(price, whPrice, productNonArmsLength);
      ({ cannabisTax, salesTax, residual } = redistributePennies({ cannabisTax, price, salesTax }));
    }
    if (discountTaxOrder === 'taxesFirst') {
      // If the tax is taken before the discount then add it into the subtotal much
      // as we do above with the discount.
      if (Big(detail.basePriceMult, 'Base Price Mult').eq(1)) {
        bottleDepositTaxCentsTotal = bottleDepositTaxCentsTotal.add(bottleDepositTaxCents);
        cannabisTaxTotal = cannabisTaxTotal.add(cannabisTax);
        salesTaxTotal = salesTaxTotal.add(salesTax);
      } else {
        // Unclear how to deal with residual taxes in this case.
        productTotal = productTotal.add(cannabisTax).add(salesTax).add(bottleDepositTaxCents.div(100));
        avoidTaxes = true;
      }
      residualPrice = residualPrice.add(residual);
    }

    const postTaxPrice = price.plus(cannabisTax).plus(salesTax).plus(bottleDepositTaxCents.div(100));
    let compositePrice = discountTaxOrder === 'taxesFirst' ? Big(postTaxPrice) : Big(price);
    const originalCompositePrice = Big(compositePrice);

    let stowedDiscounts = [];

    const calculatePromotionDiscounts = (stowDiscounts, applyStowedDiscounts) => {
      ({ compositePrice, credit, productTotal, stowedDiscounts } = calculateSaleDiscounts({
        applyStowedDiscounts,
        compositePrice,
        credit,
        detail,
        discountTaxOrder,
        originalCompositePrice,
        medicalOrder,
        productTotal,
        receiptDetail,
        specialData,
        globalSpecialsSettings,
        stowDiscounts,
        stowedDiscounts,
      }));

      ({ bogoRewardsApplied, compositePrice, credit, productTotal, stowedDiscounts } = calculateBogoDiscounts({
        applicableBogoRewards,
        applyStowedDiscounts,
        bogoRewardsApplied,
        compositePrice,
        credit,
        detail,
        discountTaxOrder,
        originalCompositePrice,
        productTotal,
        receiptDetail,
        globalSpecialsSettings,
        stowDiscounts,
        stowedDiscounts,
      }));

      ({ compositePrice, couponLeftOver, credit, productTotal, stowedDiscounts } = calculateCouponDiscounts({
        applyStowedDiscounts,
        compositePrice,
        coupon,
        couponApplicableSubtotal,
        couponApplicableSubtotalPlusTaxes,
        couponLeftOver,
        credit,
        detail,
        discountTaxOrder,
        originalCompositePrice,
        productTotal,
        receiptDetail,
        globalSpecialsSettings,
        stowDiscounts,
        stowedDiscounts,
      }));
    };
    const shouldCompareDiscounts = globalSpecialsSettings.discountStacking === false || hasIndividualNonStackingSpecial;
    // If discountStacking is explicitly set to false run to determine winningDiscount, otherwise apply defaults
    calculatePromotionDiscounts(shouldCompareDiscounts);

    // If discountStacking is explicitly set to false re-run while applying the winningDiscount
    if (shouldCompareDiscounts) {
      ({ bogoCalcsData, detail, stowedDiscounts } = compareStowedDiscounts({
        bogoCalcsData,
        detail,
        stowedDiscounts,
        globalSpecialsSettings,
      }));
      calculatePromotionDiscounts(false, true);
    }

    ({
      compositePrice,
      credit,
      productTotal,
      rewardCredit,
      rewardLeftOver,
      rewardAppliedToCredit,
    } = calculateRewardDiscounts({
      compositePrice,
      credit,
      detail,
      discountTaxOrder,
      originalCompositePrice,
      productTotal,
      receiptDetail,
      rewardApplicableSubtotal,
      rewardApplicableSubtotalPlusTaxes,
      rewardCredit,
      rewardLeftOver,
      rewards,
      rewardAppliedToCredit,
    }));

    if (discountTaxOrder === 'discountsFirst') {
      cannabisTax = detail.cannabisTax(compositePrice, whPrice, productNonArmsLength);
      salesTax = detail.salesTax(compositePrice, whPrice, productNonArmsLength);
      bottleDepositTaxCents = detail.bottleDepositTaxCents.times(detail.quantity);
      ({ cannabisTax, salesTax, residual } = redistributePennies({ cannabisTax, price: compositePrice, salesTax }));
    }
    if (discountTaxOrder === 'both' || discountTaxOrder === 'discountsFirst') {
      bottleDepositTaxCentsTotal = bottleDepositTaxCentsTotal.add(bottleDepositTaxCents);
      cannabisTaxTotal = cannabisTaxTotal.add(cannabisTax);
      salesTaxTotal = salesTaxTotal.add(salesTax);
      residualPrice = residualPrice.add(residual);
    }
    receiptDetail.addTax({ type: 'bottleDeposit', value: bottleDepositTaxCents });
    receiptDetail.addTax({ type: 'cannabis', value: cannabisTax.times(100) });
    receiptDetail.addTax({ type: detail.isVapeTaxed ? 'vape' : 'sales', value: salesTax.times(100) });

    if (bogoCalcsData.redetermineDiscounts) {
      delete bogoCalcsData.applicableRewards;
      delete bogoCalcsData.conditionsSatisfiers;
      delete bogoCalcsData.rewardsSatisfiers;
      delete bogoCalcsData.discountToCartRewards;
      delete bogoCalcsData.discountToCartSatisfiers;

      bogoCalcsData = determineBogoDiscounts({
        bogoCalcsData,
        detailsSorted,
        discountTaxOrder,
        medicalOrder,
        globalSpecialsSettings,
      });

      /*
      Before discount to cart, if we were calculating the discount on the last item in the cart and one of the specials
      was defeated, we would simply discard it. In the case of discount to cart, if we are on the last item and a DTC
      offer is defeated that means we need to redistribute the discount to the applicable items. This means we need to
      rerun the calculation one last time :pray:
     */
      const checkingLastItem = detailsSorted.length === index + 1 && detailsSorted.length > 1;
      const keysToApplyDiscount = _.keys(bogoCalcsData.discountToCartRewards);
      const atLeastOneItemCanReceiveDiscountToCart = keysToApplyDiscount.length > 0;

      if (checkingLastItem && atLeastOneItemCanReceiveDiscountToCart) {
        const adjustment = redistributeDiscountToCart({
          keysToApplyDiscount,
          detailsSorted,
          bogoCalcsData,
          products,
          discountTaxOrder,
          bottleDepositTaxCentsTotal,
          cannabisTaxTotal,
          salesTaxTotal,
          productTotal,
          avoidTaxes,
          residualPrice,
          credit,
          bogoRewardsApplied,
          compositePrice,
          stowedDiscounts,
          receiptDetail,
          globalSpecialsSettings,
        });
        bottleDepositTaxCentsTotal = adjustment.bottleDepositTaxCentsTotal;
        cannabisTaxTotal = adjustment.cannabisTaxTotal;
        salesTaxTotal = adjustment.salesTaxTotal;
        productTotal = adjustment.productTotal;
        avoidTaxes = adjustment.avoidTaxes;
        residualPrice = adjustment.residualPrice;
        credit = adjustment.credit;
        bogoRewardsApplied = adjustment.bogoRewardsApplied;
        compositePrice = adjustment.compositePrice;
        stowedDiscounts = adjustment.stowedDiscounts;
      }
    }
  });

  ({ credit } = calculateStackingCompoundingPercentDtc({
    applicableRewards: bogoCalcsData.applicableRewards,
    cartProducts: products,
    credit,
    detailItems: detailsSorted,
    discountTaxOrder,
    discountToCartSatisfiers: bogoCalcsData.discountToCartSatisfiers,
    receipt,
  }));

  const menuTotal = calculateMenuTotal(details, globalSpecialsSettings);

  const {
    productTotal: productTotalPlusResidual,
    cannabisTaxTotal: cannabisTaxTotalPlusResidual,
    salesTaxTotal: salesTaxPlusResidual,
  } = findAPlaceForResidualPennies(avoidTaxes, productTotal, residualPrice, cannabisTaxTotal, salesTaxTotal, receipt);

  productTotal = productTotalPlusResidual;
  cannabisTaxTotal = cannabisTaxTotalPlusResidual;
  salesTaxTotal = salesTaxPlusResidual;

  const subtotalMinusCredit = productTotal.minus(credit);

  let deliveryFee = calculateDeliveryFee(deliveryOption, dispensary, subtotalMinusCredit);
  salesTaxTotal = addCanadianDeliveryFeeTaxIfApplicable(dispensary, deliveryFee, salesTaxTotal, receipt);

  salesTaxTotal = salesTaxTotal.round(2);
  cannabisTaxTotal = cannabisTaxTotal.round(2);
  credit = credit.round(2);
  rewardCredit = rewardCredit.round(2);

  const { posttaxSubtotal, negativePosttaxSubtotal } = calculatePostTaxSubtotal(
    productTotal,
    salesTaxTotal,
    cannabisTaxTotal,
    bottleDepositTaxCentsTotal,
    credit
  );

  const altBasisForZeroedSubtotal =
    discountTaxOrder === 'both' || discountTaxOrder === 'taxesFirst'
      ? productTotal.plus(cannabisTaxTotal).plus(salesTaxTotal).plus(bottleDepositTaxCentsTotal.div(100))
      : productTotal;

  // handles case where a tip was selected, but then cash or check was selected instead after, which could
  // apply the tip erroneously
  const shouldApplyTip = tipValue && !_.includes([`cash`, `check`], paymentMethod);
  let tip = calculateTip(shouldApplyTip, tipValue, negativePosttaxSubtotal, altBasisForZeroedSubtotal, posttaxSubtotal);

  const paymentFee = calculatePaymentFeeIfApplicable(
    dispensary,
    paymentMethod,
    negativePosttaxSubtotal,
    altBasisForZeroedSubtotal,
    posttaxSubtotal,
    receipt
  );

  deliveryFee = deliveryFee.round(2);
  tip = tip.round(2);
  paymentFee.round(0);
  let total = posttaxSubtotal.plus(deliveryFee).plus(tip).plus(paymentFee.div(100));

  receipt.addFee({ type: `delivery`, value: deliveryFee.times(100) });
  receipt.addFee({ type: `tip`, value: tip.times(100) });
  // If a discount exceeds the subtotal, we zero it out above.
  // Add tax back in if the dispensary calculates tax pre-discount.
  if (negativePosttaxSubtotal && (discountTaxOrder === 'both' || discountTaxOrder === 'taxesFirst')) {
    total = total.plus(cannabisTaxTotal).plus(salesTaxTotal).plus(bottleDepositTaxCentsTotal.div(100));
  }

  /*
   * These are hand discounts applied by the dispensary.  The dispensary can
   * do whatever they want for a discount up to making the order free.
   */
  if (manualDiscount) {
    const { newCredit } = calculateManualDiscounts(manualDiscount, total, receipt);
    total = total.minus(newCredit);
    credit = credit.plus(newCredit);
  }

  // remove reward from credit for discount line item
  if (rewardAppliedToCredit) {
    credit = credit.minus(rewardCredit);
  }

  receipt.finalize(total);

  const mixAndMatchTotal = Big(receipt?.summary?.mixAndMatch || 0)
    .div(100)
    .round(2);

  return {
    menuTotal: Number(menuTotal),
    cartSubtotal: Number(productTotal),
    subtotal: Number(productTotal),
    bottleDepositTaxCents: Number(bottleDepositTaxCentsTotal),
    cannabisTax: Number(cannabisTaxTotal),
    tax: Number(salesTaxTotal),
    deliveryFee: Number(deliveryFee),
    credit: Number(credit),
    mixAndMatch: Number(mixAndMatchTotal),
    reward: Number(rewardCredit),
    tip: Number(tip),
    paymentFee: Math.trunc(Number(paymentFee)),
    total: Number(total),
    details,
    lineitems: [
      { type: `productSubtotal`, value: Number(productTotal) },
      { type: `mixAndMatch`, value: Number(mixAndMatchTotal) },
      {
        type: `discount`,
        value: Number(credit),
      },
      {
        type: `reward`,
        value: Number(rewardCredit),
        brand: !_.isEmpty(appliedRewards) ? appliedRewards[0]?.brand : `Reward`,
      },
      { type: 'paymentFee', value: Math.trunc(Number(paymentFee)) },
      { type: 'deliveryFee', value: Number(deliveryFee) },
      { type: 'tip', value: Number(tip) },
      { type: 'bottleDepositTaxCents', value: Number(bottleDepositTaxCentsTotal) },
      { type: 'cannabisTax', value: Number(cannabisTaxTotal) },
      { type: 'salesTax', value: Number(salesTaxTotal) },
      { type: 'total', value: Number(total) },
    ],
    salesTaxExistence,
    cannabisTaxExistence,
    bottleDepositTaxExistence,
    receipt: receipt.toJSON(),
  };
}
